import { Component, Input, ViewChild } from '@angular/core';
import { AjaxService } from 'AjaxService';
import { DxFormComponent } from 'devextreme-angular/ui/form';
import { SpinnerService } from '@services/spinner.service';
import { EntityArt } from '@enums/entityArt.enum';
import DataSource from 'devextreme/data/data_source';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { lastValueFrom } from 'rxjs';

export interface IBemerkungenRequestViewModel {
  ownerId: number;
  entityArt: EntityArt;

  searchText: string;
  skip: number;
  take: number;
  sortColumn: string;
  sortDirection: boolean;
}

interface IBemerkungenResponseViewModel {
  items: IBemerkungenViewModel[];
  totalItems: number;
}

export interface IBemerkungenViewModel {
  id: number;
  ownerId: number;
  entityArt: EntityArt;

  benutzerName: string;
  erstellt: Date;
  betreff: string;
  text: string;
  gehoertZu: string;
  owi104?: boolean;
}

@Component({
  selector: 'cz-bemerkungen',
  templateUrl: 'bemerkungen.component.html',
  standalone: false,
})
export class BemerkungenComponent {
  @ViewChild('form', { static: false }) form: DxFormComponent;

  @Input() ownerId: number;
  @Input() entityArt: EntityArt;
  @Input() disabled: boolean = false;

  loading = false;
  dataSource: any = {};

  constructor(
    private ajaxService: AjaxService,
    private _sanitizer: DomSanitizer,
    private spinnerService: SpinnerService
  ) {
    const self = this;

    this.dataSource = new DataSource({
      load: function (loadOptions: any) {
        const searchRequest: IBemerkungenRequestViewModel = {
          entityArt: self.entityArt,
          ownerId: self.ownerId,
          searchText: loadOptions.filter && loadOptions.filter.length > 0 ? loadOptions.filter[0][2] : '',
          skip: loadOptions.skip,
          take: loadOptions.take,
          sortColumn: loadOptions.sort ? loadOptions.sort[0].selector : '',
          sortDirection: loadOptions.sort ? loadOptions.sort[0].desc : false,
        };

        return lastValueFrom(self.ajaxService.post<any>('api/Bemerkungen/Search', searchRequest)).then((response) => {
          return {
            data: response.items,
            totalCount: response.totalItems,
          };
        });
      },
      insert: function (rowData) {
        const bemerkung = {
          entityArt: self.entityArt,
          ownerId: self.ownerId,
          betreff: rowData.betreff,
          text: rowData.text,
          owi104: rowData.owi104,
        } as IBemerkungenViewModel;

        return lastValueFrom(self.ajaxService.post('api/Bemerkungen/Create', bemerkung)).then((response) => {
          self.dataSource.reload();
        });
      },
      update: function (key, rowData) {
        if (rowData) {
          if (rowData.betreff) {
            key.betreff = rowData.betreff;
          }
          if (rowData.text) {
            key.text = rowData.text;
          }

          key.owi104 = rowData.owi104;
        }

        return lastValueFrom(self.ajaxService.post('api/Bemerkungen/Update', key))
          .then((response) => {
            self.dataSource.reload();
          })
          .catch((err) => {
            setTimeout(() => {
              throw err;
            }, 0);
          });
      },
    });
  }

  onClickPrint(data: IBemerkungenViewModel) {
    const downloadLink = document.createElement('a');
    const blob = new Blob([
      '\ufeff',
      'Erstellt: ' +
        new Date(data.erstellt).toLocaleDateString('de-DE', { year: 'numeric', month: '2-digit', day: '2-digit' }) +
        '\r\n' +
        'Gehört zu: ' +
        data.gehoertZu +
        '\r\n\r\n' +
        'Betreff: ' +
        data.betreff +
        '\r\n\r\n' +
        data.text,
    ]);
    const url = URL.createObjectURL(blob);

    downloadLink.href = url;
    downloadLink.download = this.GetFilename(data);
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  }

  GetFilename(data: IBemerkungenViewModel) {
    const re = /\./gi;
    let fileName = new Date().toLocaleDateString('de-DE', { year: 'numeric', month: '2-digit', day: '2-digit' }).replace(re, '_');
    fileName += '_' + EntityArt.shortKey(data.entityArt) + '_' + data.betreff + '.txt';
    return fileName;
  }

  getText(data) {
    return this.sanitizeHtml(data);
  }

  sanitizeHtml(html): SafeHtml {
    return this._sanitizer.bypassSecurityTrustHtml(html);
  }

  onEditorPreparing(e) {
    if (e.dataField === 'text') {
      e.editorName = 'dxTextArea';
    }
  }

  onToolbarPreparing(e) {
    const toolbarItems = e.toolbarOptions.items;
    const searchPanel = toolbarItems.filter((item) => item.name === 'searchPanel');

    if (searchPanel && searchPanel[0]) {
      searchPanel[0].location = 'before';
    }
  }

  onInitNewRow(e) {
    e.data.owi104 = false;
  }

  private startLoading() {
    this.spinnerService.show();
    this.loading = true;
  }

  private stopLoading() {
    this.spinnerService.hide();
    this.loading = false;
  }
}
